import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";

import { toast } from "@/components/toast/CustomToast";
import {
  applySequenceToVisibleRows,
  buildReorderPayload,
  findTargetId,
  mergeVisibleOrderIntoSequenceRows,
  rowId,
  swapRows,
  type RosterCreateNurseRow,
  type RosterCreatePendingSequenceSave,
  type RosterCreateRowMoveDirection,
} from "../utils/rosterCreateNurseReorderUtils";
import { useRosterCreateNurseSequenceCache } from "./useRosterCreateNurseSequenceCache";
import {
  enqueueRosterNurseSequenceSave,
  flushRosterNurseSequenceOnPageHide,
  persistRosterNurseSequenceSave,
  replayPersistedRosterNurseSequenceSave,
  waitForRosterNurseSequenceQueue,
} from "../services/rosterNurseSequenceSaveQueue";

type UseRosterCreateNurseReorderOptions<T extends RosterCreateNurseRow> = {
  readonly copyNurses: readonly T[];
  readonly setCopyNurses: Dispatch<SetStateAction<T[]>>;
  readonly copyNursesRef?: MutableRefObject<readonly T[]>;
  readonly sequenceSourceRows?: readonly RosterCreateNurseRow[];
  readonly groupId?: string | null;
  readonly debounceMs?: number;
};

const REORDER_SAVE_IDLE_MS = 1200;

export function useRosterCreateNurseReorder<T extends RosterCreateNurseRow>({
  copyNurses,
  setCopyNurses,
  copyNursesRef,
  sequenceSourceRows,
  groupId,
  debounceMs = REORDER_SAVE_IDLE_MS,
}: UseRosterCreateNurseReorderOptions<T>) {
  const [isSavingRosterNurseSequence, setIsSavingRosterNurseSequence] =
    useState(false);
  const [pendingRosterSequenceNurseIds, setPendingRosterSequenceNurseIds] =
    useState<ReadonlySet<string>>(() => new Set());

  const latestRowsRef = useRef<readonly T[]>(copyNurses);
  const sequenceSourceRef = useRef<readonly RosterCreateNurseRow[]>(
    sequenceSourceRows ?? copyNurses
  );
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<RosterCreatePendingSequenceSave | null>(null);
  const inFlightRef = useRef(false);
  const isDirtyRef = useRef(false);
  const revisionRef = useRef(0);
  const { invalidateSequenceCaches, patchCaches } =
    useRosterCreateNurseSequenceCache(groupId);

  useEffect(() => {
    if (isDirtyRef.current) return;
    latestRowsRef.current = copyNurses;
    sequenceSourceRef.current = sequenceSourceRows ?? copyNurses;
  }, [copyNurses, sequenceSourceRows]);

  const applyRows = useCallback(
    (rows: readonly T[]) => {
      const nextRows = [...rows];
      latestRowsRef.current = nextRows;
      if (copyNursesRef) {
        copyNursesRef.current = nextRows;
      }
      setCopyNurses(() => nextRows);
    },
    [copyNursesRef, setCopyNurses]
  );

  const flushSave = useCallback(() => {
    if (inFlightRef.current) return;
    if (debounceTimerRef.current) return;
    const save = pendingSaveRef.current;
    if (!save) return;

    pendingSaveRef.current = null;
    inFlightRef.current = true;

    void (async () => {
      try {
        await enqueueRosterNurseSequenceSave(save.payload, groupId);
        const hasPendingDebounce = debounceTimerRef.current != null;
        const isLatestSave = save.revision === revisionRef.current;
        if (!pendingSaveRef.current && !hasPendingDebounce && isLatestSave) {
          isDirtyRef.current = false;
          setIsSavingRosterNurseSequence(false);
          setPendingRosterSequenceNurseIds(new Set());
        }
      } catch (error) {
        if (save.revision === revisionRef.current) {
          pendingSaveRef.current = null;
          isDirtyRef.current = false;
          setIsSavingRosterNurseSequence(false);
          setPendingRosterSequenceNurseIds(new Set());
          await waitForRosterNurseSequenceQueue(groupId);
          await invalidateSequenceCaches();
          toast.error("근무표 행 순서 저장에 실패했습니다.");
        } else if (error instanceof Error) {
          console.error(
            "Stale roster create nurse sequence save failed:",
            error
          );
        } else {
          console.error(
            "Stale roster create nurse sequence save failed:",
            String(error)
          );
        }
      } finally {
        inFlightRef.current = false;
        if (pendingSaveRef.current && !debounceTimerRef.current) {
          flushSave();
        }
      }
    })();
  }, [groupId, invalidateSequenceCaches]);

  const scheduleSave = useCallback(
    (save: RosterCreatePendingSequenceSave) => {
      pendingSaveRef.current = save;
      isDirtyRef.current = true;
      persistRosterNurseSequenceSave(save.payload, groupId);
      setIsSavingRosterNurseSequence(true);
      setPendingRosterSequenceNurseIds(new Set(save.pendingIds));

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        flushSave();
      }, debounceMs);
    },
    [debounceMs, flushSave, groupId]
  );

  const applyAndScheduleRows = useCallback(
    (rows: readonly T[], pendingIds: readonly string[]) => {
      const currentSequenceRows = sequenceSourceRef.current;

      const nextSequenceRows = mergeVisibleOrderIntoSequenceRows(
        currentSequenceRows,
        rows
      );
      const nextRows = applySequenceToVisibleRows(rows, nextSequenceRows);
      sequenceSourceRef.current = nextSequenceRows;
      applyRows(nextRows);
      patchCaches(nextSequenceRows);

      revisionRef.current += 1;
      scheduleSave({
        revision: revisionRef.current,
        payload: buildReorderPayload(nextSequenceRows),
        pendingIds,
      });
    },
    [applyRows, patchCaches, scheduleSave]
  );

  const flushPendingSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    flushSave();
  }, [flushSave]);

  const flushPendingSaveOnPageHide = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const save = pendingSaveRef.current;
    if (!save) return;
    const request = flushRosterNurseSequenceOnPageHide(save.payload, groupId);
    if (!request) return;

    pendingSaveRef.current = null;
    inFlightRef.current = true;
    void request
      .then(() => {
        if (save.revision !== revisionRef.current) return;
        isDirtyRef.current = false;
        setIsSavingRosterNurseSequence(false);
        setPendingRosterSequenceNurseIds(new Set());
      })
      .catch(async () => {
        if (save.revision !== revisionRef.current) return;
        isDirtyRef.current = false;
        await waitForRosterNurseSequenceQueue(groupId);
        await invalidateSequenceCaches();
        setIsSavingRosterNurseSequence(false);
        setPendingRosterSequenceNurseIds(new Set());
      })
      .finally(() => {
        inFlightRef.current = false;
        if (pendingSaveRef.current && !debounceTimerRef.current) flushSave();
      });
  }, [flushSave, groupId, invalidateSequenceCaches]);

  const moveRosterNurseRow = useCallback(
    (
      nurseId: string,
      direction: RosterCreateRowMoveDirection,
      visibleNurseIds: readonly string[],
      captureRowPositions?: () => void
    ) => {
      const targetId = findTargetId(nurseId, direction, visibleNurseIds);
      if (!targetId) return;

      const rows = latestRowsRef.current;
      const nextRows = swapRows(rows, nurseId, targetId);
      if (!nextRows) return;

      captureRowPositions?.();
      applyAndScheduleRows(nextRows, [nurseId, targetId]);
    },
    [applyAndScheduleRows]
  );

  const sortRosterNurseRows = useCallback(
    (rows: readonly T[]) => {
      applyAndScheduleRows(rows, rows.map(rowId));
    },
    [applyAndScheduleRows]
  );

  useEffect(() => {
    const handlePageHide = () => {
      flushPendingSaveOnPageHide();
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      flushPendingSave();
    };
  }, [flushPendingSave, flushPendingSaveOnPageHide]);

  useEffect(() => {
    let isCurrent = true;
    void replayPersistedRosterNurseSequenceSave(groupId)
      .then(async (replayed) => {
        if (isCurrent && replayed) await invalidateSequenceCaches();
      })
      .catch(async () => {
        if (!isCurrent) return;
        await waitForRosterNurseSequenceQueue(groupId);
        await invalidateSequenceCaches();
      });
    return () => {
      isCurrent = false;
    };
  }, [groupId, invalidateSequenceCaches]);

  return {
    moveRosterNurseRow,
    sortRosterNurseRows,
    isSavingRosterNurseSequence,
    pendingRosterSequenceNurseIds,
  };
}
